import Alpine from "alpinejs";
import debounce from "lodash.debounce";
import { AlpineComponent } from "@nxtlvlsoftware/alpine-typescript";
import {
  ANIMATE,
  HEAD_HEIGHT,
  SELECTED_CLS,
  SelectionMethod,
  type TreeNodeSelectedEvent,
} from "./TreeNode";
import { DEBOUNCE_SCROLL_ANIMATED_MS, DEBOUNCE_SCROLL_MS } from "../timing";
import type TreeNode from "./TreeNode";
import * as q from "../utils/qs";

export default class TreeRoot extends AlpineComponent {
  private _onScrollHandler = debounce(() => { }, 0);

  constructor(
    public scrollPaddingTop = 0,
    public scroll = 0,
    public step = 28,
    public persist: string | null = null
  ) {
    super();
  }

  async init() {
    this._onScrollHandler = ANIMATE
      ? debounce(this.onScroll.bind(this), DEBOUNCE_SCROLL_ANIMATED_MS)
      : debounce(this.onScroll.bind(this), DEBOUNCE_SCROLL_MS)

    // Note: this is important to the sticky/last and
    // other HTML DOM mutation that could affect the scroll
    // behavior must be executed prior to setting the initial
    // scroll position.
    if (!ANIMATE) {
      this.$el.classList.add("no-animations");
    }

    await this.applyInitialScrollPosition();

    // only then we activate the scroll listener
    this.activateScrollListener();
  }

  destroy() {
    this.$el.removeEventListener("scroll", this._onScrollHandler);
    this._onScrollHandler.cancel();
  }

  activateScrollListener() {
    this.$el.addEventListener("scroll", this._onScrollHandler);
  }

  selected() {
    const elem = this.$el.querySelector("." + SELECTED_CLS);
    if (!elem) return null;
    return Alpine.$data(elem as HTMLElement) as TreeNode;
  }

  style() {
    const parts: string[] = [];
    parts.push(`scroll-padding-top: ${this.scrollPaddingTop}px`);
    return parts.join("; ");
  }

  ensureVisible(sel: TreeNode, behavior: ScrollBehavior = "instant") {
    return new Promise<void>((resolve) => {
      const head = sel.$refs.head as HTMLElement;
      const headPos = head.style.position;

      // We deactivate the sticky to get the real position of the head
      head.style.position = "";
      const headRect = head!.getBoundingClientRect();
      head.style.position = headPos;

      const containerRect = this.$el.getBoundingClientRect();
      const pTop = sel.level() * HEAD_HEIGHT;
      const top = headRect.top - containerRect.top - pTop;
      const bottom = headRect.bottom - containerRect.bottom;

      let scrollTop = 0;
      if (top < 0) {
        scrollTop = top;
      } else if (bottom > 0) {
        scrollTop = bottom;
      }

      if (scrollTop === 0) {
        resolve();
        return;
      }

      requestAnimationFrame(() => {
        this.$el.style.scrollSnapType = "none";
        this.$el.addEventListener(
          "scrollend",
          () => {
            this.$el.style.scrollSnapType = "y mandatory";
            this.$el.setAttribute("aria-busy", "false");
            resolve();
          },
          { once: true }
        );
        this.$el.setAttribute("aria-busy", "true");
        this.$el.scrollBy({ top: scrollTop, behavior: behavior });
      });
    });
  }

  applyStickyAndLast() {
    const containerTop = this.$el.getBoundingClientRect().top;
    const openNodes = this.$el.querySelectorAll(
      ".tree-node.open"
    ) as NodeListOf<HTMLElement>;
    const results: TreeNode[] = [];

    for (const nodeElem of openNodes) {
      const node = Alpine.$data(nodeElem) as TreeNode;
      node.isLastSticky = false;
      const top = node.$refs.head.getBoundingClientRect().top;
      const level = node.level();
      const stepsFromTop = (top - containerTop) / 28;
      if (stepsFromTop === level) {
        results.push(node);
      } else {
        node.isSticky = false;
        node.isLastSticky = false;
      }
    }

    for (let i = 0; i < results.length; i++) {
      const node = results[i];
      node.isSticky = true;
      node.isLastSticky = i + 1 === results.length;
    }
    this.scrollPaddingTop =
      results.filter((x) => x.path.reduce((a, b) => a + b, 0) === 0).length *
      28;
  }

  onScroll() {
    this.$el.setAttribute("aria-busy", "true");
    this.applyStickyAndLast();
  }

  onScrollEnd() {
    this.$el.setAttribute("aria-busy", "false");
    if (this.persist !== null) {
      const nSteps = Math.round(this.$el.scrollTop / this.step).toString();
      q.setParam(this.persist, nSteps);
    }
  }

  onTreeNodeSelected(event: TreeNodeSelectedEvent) {
    let sel = this.selected();
    if (sel) {
      sel.isSelected = false;
    }
    sel = event.detail.node;
    sel.isSelected = true;
    const behavior =
      event.detail.method === SelectionMethod.Click && ANIMATE
        ? "smooth"
        : "instant";
    this.ensureVisible(sel, behavior);
  }

  onUp(event: KeyboardEvent) {
    event.preventDefault();
    const sel = this.selected();
    if (!sel) return;

    let prev = sel.previous();

    if (!prev) {
      prev = sel.parent();
      if (!prev) {
        return;
      }
    } else if (prev.isOpen) {
      let lc = prev.lastChild();
      while (prev.isOpen && lc) {
        prev = lc;
        lc = prev.lastChild();
      }
    }

    prev.select(SelectionMethod.Keyboard);
  }

  async onRight(event: KeyboardEvent) {
    event.preventDefault();
    const sel = this.selected();
    if (!sel) return;

    if (sel.n == 0) return;

    if (!sel.isOpen) {
      await sel.open();
      sel.select(SelectionMethod.Keyboard);
      return;
    }

    const firstChild = sel.firstChild();
    if (firstChild) {
      firstChild.select(SelectionMethod.Keyboard);
    }
  }

  onDown(event: KeyboardEvent) {
    event.preventDefault();
    const sel = this.selected();
    if (!sel) return;

    if (sel.isOpen) {
      const first = sel.firstChild();
      if (first) {
        first.select(SelectionMethod.Keyboard);
        return;
      }
    }

    let item: TreeNode | null = sel;
    let next: TreeNode | null = item.next();
    while (!next && item) {
      item = item?.parent();
      next = item?.next() || null;
    }

    if (next) {
      next.select(SelectionMethod.Keyboard);
      return;
    }
  }

  async onLeft(event: KeyboardEvent) {
    event.preventDefault();
    const sel = this.selected();
    if (!sel) return;

    if (sel.isOpen) {
      await sel.close();
      sel.select(SelectionMethod.Keyboard);
      return;
    }

    const parent = sel.parent();
    if (parent) {
      parent.select(SelectionMethod.Keyboard);
      return;
    }
  }

  async applyInitialScrollPosition() {
    // Set scroll position from scroll value
    if (this.scroll > 0) {
      this.$el.scrollTo({ top: this.scroll * this.step, behavior: "instant" });
      return;
    }

    // Set scroll position from selected tree-node
    await this.$nextTick();
    const selectedElement = this.$el.querySelector(
      ".tree-node.selected"
    ) as HTMLElement | null;
    if (selectedElement === null) return;

    const node = Alpine.$data(selectedElement) as TreeNode;
    await this.ensureVisible(node, "instant");
  }
}
